<template>
	<v-card elevation="1" rounded="lg" class="fill-height d-flex flex-column">
		<v-card-title class="d-flex align-center py-3 px-4">
			<v-icon color="primary" size="20" class="me-2">mdi-calendar-star</v-icon>
			<span class="text-subtitle-2">{{ $t("dashboard.activeRitual.title") }}</span>
			<v-spacer />
			<span v-if="activeRitual && !loading" class="live-badge text-caption">
				<span class="live-badge__dot" />
				{{ $t("dashboard.activeRitual.live") }}
			</span>
		</v-card-title>

		<v-divider />

		<v-card-text class="flex-grow-1 pa-0">
			<!-- Loading -->
			<div v-if="loading" class="pa-4">
				<v-skeleton-loader type="list-item-three-line" />
			</div>

			<!-- Ritual em andamento -->
			<template v-else-if="activeRitual">
				<div class="d-flex align-center ga-4 pa-4">
					<v-progress-circular
						:model-value="progress"
						:size="72"
						:width="7"
						color="primary"
						class="flex-shrink-0"
					>
						<span class="text-body-2 font-weight-bold">{{ progress }}%</span>
					</v-progress-circular>

					<div class="min-width-0">
						<div class="text-body-2 font-weight-medium text-truncate">
							{{ activeRitual.title }}
						</div>
						<div class="text-caption text-medium-emphasis text-truncate">
							{{ activeRitual.ritualType.name }}
						</div>
						<div class="text-caption text-medium-emphasis mt-1 d-flex align-center">
							<v-icon size="12" class="me-1">mdi-clock-outline</v-icon>
							{{ startedLabel }}
							<span class="mx-1">&middot;</span>
							{{ elapsedLabel }}
						</div>
					</div>
				</div>

				<v-divider />

				<div v-if="activeRitual.totalCount > 0" class="d-flex text-center">
					<div v-for="stat in stats" :key="stat.key" class="flex-1-1 py-3">
						<div class="text-h6 font-weight-bold" :class="`text-${stat.color}`">
							{{ stat.value }}
						</div>
						<div class="text-caption text-medium-emphasis">{{ stat.label }}</div>
					</div>
				</div>

				<div v-else class="text-caption text-medium-emphasis text-center py-4">
					{{ $t("dashboard.activeRitual.noTickets") }}
				</div>
			</template>

			<!-- Sem ritual em andamento -->
			<div v-else class="d-flex flex-column align-center justify-center text-center pa-6" style="min-height: 140px">
				<v-icon size="40" color="grey-lighten-1" class="mb-2">mdi-candle</v-icon>
				<span class="text-body-2 text-medium-emphasis">{{ $t("dashboard.activeRitual.noRituals") }}</span>
				<span class="text-caption text-medium-emphasis mt-1">
					{{ $t("dashboard.activeRitual.noRitualsHint") }}
				</span>
			</div>
		</v-card-text>

		<v-divider v-if="!loading" />

		<v-card-actions v-if="activeRitual && !loading" class="pa-2 ga-2">
			<v-btn
				:to="`/ritual/${activeRitual.id}/atendimento`"
				color="primary"
				variant="elevated"
				size="small"
				prepend-icon="mdi-account-heart-outline"
				class="flex-1-1"
			>
				{{ $t("ritualAttendance.attendance") }}
			</v-btn>
			<v-btn
				:to="`/ritual/${activeRitual.id}/chamada`"
				color="primary"
				variant="tonal"
				size="small"
				prepend-icon="mdi-bullhorn-outline"
				class="flex-1-1"
			>
				{{ $t("ticketCall.title") }}
			</v-btn>
		</v-card-actions>

		<v-card-actions v-else-if="!loading" class="pa-2 justify-center">
			<v-btn variant="text" size="small" color="primary" to="/rituals">
				{{ $t("dashboard.activeRitual.viewAll") }}
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useNotifications } from "../../composables/useNotifications";
import { useDateFormat } from "@/composables/useDateFormat";
import type { ActiveRitual } from "@/types/ticket";
import { ticketService } from "@/services/ticketService";

// Composables
const { t } = useI18n();
const { showError } = useNotifications();
const { formatTimestamp } = useDateFormat();

// State
const activeRitual = ref<ActiveRitual | null>(null);
const loading = ref(false);
const now = ref(Date.now());
let elapsedTimer: ReturnType<typeof setInterval> | undefined;

// Computed
const progress = computed(() => {
	const ritual = activeRitual.value;
	if (!ritual || ritual.totalCount === 0) return 0;
	return Math.round((ritual.completedCount / ritual.totalCount) * 100);
});

const stats = computed(() => {
	const ritual = activeRitual.value;
	if (!ritual) return [];
	return [
		{ key: "pending", value: ritual.pendingCount, color: "warning", label: t("dashboard.activeRitual.pending") },
		{ key: "inProgress", value: ritual.inProgressCount, color: "info", label: t("dashboard.activeRitual.inProgress") },
		{ key: "completed", value: ritual.completedCount, color: "success", label: t("dashboard.activeRitual.completed") },
	];
});

const startedLabel = computed(() => {
	if (!activeRitual.value) return "";
	const start = new Date(activeRitual.value.startDate);
	const isToday = start.toDateString() === new Date(now.value).toDateString();

	// Só a hora quando o ritual abriu hoje; caso contrário a data também,
	// senão "Aberto às 19:30" engana em um ritual aberto há dias.
	const time = formatTimestamp(activeRitual.value.startDate, {
		hour: "2-digit",
		minute: "2-digit",
	});
	if (isToday) {
		return t("dashboard.activeRitual.openSince", { time });
	}
	const date = formatTimestamp(activeRitual.value.startDate, {
		day: "2-digit",
		month: "2-digit",
	});
	return t("dashboard.activeRitual.openSinceDate", { date, time });
});

const elapsedLabel = computed(() => {
	if (!activeRitual.value) return "";
	const elapsedMs = Math.max(0, now.value - new Date(activeRitual.value.startDate).getTime());
	const totalMinutes = Math.floor(elapsedMs / 60000);

	if (totalMinutes < 60) {
		return t("dashboard.activeRitual.elapsedMinutes", { minutes: totalMinutes });
	}

	const totalHours = Math.floor(totalMinutes / 60);
	// Acima de um dia, contar em dias — "há 168h19" não diz nada a ninguém
	if (totalHours >= 24) {
		const days = Math.floor(totalHours / 24);
		const key = days === 1 ? "elapsedDay" : "elapsedDays";
		return t(`dashboard.activeRitual.${key}`, { days });
	}

	const minutes = String(totalMinutes % 60).padStart(2, "0");
	return t("dashboard.activeRitual.elapsedHours", { hours: totalHours, minutes });
});

// Methods
async function loadActiveRitual() {
	loading.value = true;
	try {
		const rituals = await ticketService.getActiveRituals();
		// Pegar o primeiro ritual ativo
		activeRitual.value = rituals.length > 0 ? rituals[0] : null;
	} catch (error: any) {
		if (error.response?.status !== 404) {
			console.error("Erro ao carregar rituais ativos:", error);
			showError(t("dashboard.activeRitual.loadError"));
		}
	} finally {
		loading.value = false;
	}
}

// Lifecycle
onMounted(() => {
	loadActiveRitual();
	elapsedTimer = setInterval(() => {
		now.value = Date.now();
	}, 60000);
});

onUnmounted(() => {
	if (elapsedTimer) clearInterval(elapsedTimer);
});
</script>

<style scoped>
.min-width-0 {
	min-width: 0;
}

.live-badge {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: rgb(var(--v-theme-error));
	font-weight: 600;
	letter-spacing: 0.5px;
	text-transform: uppercase;
}

.live-badge__dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: rgb(var(--v-theme-error));
	/* keyframes globais em styles/_animations.scss */
	--ping-ring-color: rgba(var(--v-theme-error), 0.4);
	animation: pingRing 2s ease-out infinite;
}

@media (prefers-reduced-motion: reduce) {
	.live-badge__dot {
		animation: none;
	}
}
</style>
